/*
  eslint-disable @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-call, import/no-cycle, @typescript-eslint/no-unsafe-return,
  no-param-reassign, no-nested-ternary
*/

import { v4 as uuidv4 } from 'uuid';
import { Branch, QUESTIONNAIRE_ELEMENT_TYPE, SCREENING_TYPE } from '../../questionnaireTemplate.enums';
import { QuestionnaireEntities, QuestionnaireFormElement, QuestionnaireOrdering } from '../../questionnaireTemplate.types';
import { formElementSchema } from '../schemas/formElement.schema';

/**
 * Derives the SCREENING_TYPE from knockout / isPausable flags returned by the API.
 */
const toScreeningType = (knockout: boolean, isPausable: boolean): SCREENING_TYPE => {
  if (knockout) return SCREENING_TYPE.KNOKOUT;
  if (isPausable) return SCREENING_TYPE.REVIEW;
  return SCREENING_TYPE.NONE;
};

/**
 * Recursively normalizes a flat-ish API question array (with nested followups)
 * back into the normalized entities + ordering shape used by the store.
 *
 * @param questions      - Array of API questions at this level (root or followup children)
 * @param entities       - Accumulator for normalized element entities (mutated in place)
 * @param ordering       - Accumulator for ordering map (mutated in place)
 * @param parentId       - ID of the parent element, or null for root questions
 * @param parentBranch   - Branch name the parent used to reach this level, or null
 * @param isCopy         - When true, re-generates all IDs so the result is a fresh copy
 * @returns              - Array of element IDs at this level (in order)
 */
const normalizeLevel = (
  questions: any[],
  entities: QuestionnaireEntities,
  ordering: QuestionnaireOrdering,
  parentId: string | null,
  parentBranch: string | null,
  isCopy: boolean,
): string[] => {
  // Sort by questionOrder so we preserve the original intended order.
  const sorted = [...questions].sort((a, b) => (a.questionOrder ?? 0) - (b.questionOrder ?? 0));

  return sorted.map((q) => {
    const id = isCopy ? uuidv4() : q.id ?? uuidv4();

    // --- Reconstruct options ---
    const options = (q.options ?? []).map((opt: any) => {
      const optId = isCopy ? uuidv4() : opt.id ?? uuidv4();
      return {
        id: optId,
        text: opt.text ?? '',
        priority: opt.priority,
        isPreferredAnswer: (q.preferredAnswers ?? []).includes(opt.id),
      };
    });

    // --- Reconstruct activeBranches from followups ---
    //   Each followup carries an `optionId` pointing at the branch option, or a
    //   branch name derived from yes/no preferred-answer semantics.
    const activeBranches: Branch[] = [];

    const followups: any[] = q.followups ?? [];
    followups.forEach((child: any) => {
      if (child.optionId) {
        // For multi-choice: the branch name is the option text
        const matchingOption = options.find((o: any) => o.id === child.optionId);
        if (matchingOption && !activeBranches.includes(matchingOption.text)) {
          activeBranches.push(matchingOption.text);
        }
      }
    });

    // For yes/no types, we reconstruct yes/no branches
    const isYesNo =
      q.questionType === QUESTIONNAIRE_ELEMENT_TYPE.YESNO || q.questionType === QUESTIONNAIRE_ELEMENT_TYPE.YESNO_WITH_IMAGE;

    if (isYesNo && followups.length > 0) {
      // Yes/No followups use optionId as-is; branches are 'yes' | 'no'
      // We infer them from optionId matching option text
      followups.forEach((child: any) => {
        const matchingOption = options.find((o: any) => o.id === child.optionId);
        const branchName = matchingOption?.text?.toLowerCase();
        /* istanbul ignore next */
        if (branchName && (branchName === 'yes' || branchName === 'no') && !activeBranches.includes(branchName)) {
          activeBranches.push(branchName);
        }
      });
    }
    const schema = formElementSchema?.[(q.questionType as QUESTIONNAIRE_ELEMENT_TYPE) ?? QUESTIONNAIRE_ELEMENT_TYPE.FREE_FORM];

    const isDocumentUpload = q.questionType === QUESTIONNAIRE_ELEMENT_TYPE.DOCUMENT_UPLOAD;
    const isVideoResponse = q.questionType === QUESTIONNAIRE_ELEMENT_TYPE.VIDEO_RESPONSE;
    // --- Build the normalized element ---
    const element: QuestionnaireFormElement = {
      id,
      questionType: q.questionType ?? QUESTIONNAIRE_ELEMENT_TYPE.FREE_FORM,
      question: q.question ?? '',
      label: q.label ?? schema.label ?? /* istanbul ignore next */ '',
      config: q.config ?? {},
      parentId,
      parentBranch: parentBranch as any,
      expanded: true,
      screeningType: toScreeningType(!!q.knockout, !!q.isPausable),
      shouldReaskOnReapply: q.shouldReaskOnReapply ?? false,
      maxLength: q.maxLength,
      activeBranches: activeBranches.length > 0 ? activeBranches : undefined,
      options: options.length > 0 ? options : undefined,
      images: q.images,
      totalAnswer: q.totalAnswer,
      correctAnswerIf: q.correctAnswerIf,
      shouldMatchAtLeast: q.shouldMatchAtLeast,
      // yes/no: API stores boolean, store keeps 'yes' | 'no' string
      preferredAnswer: isYesNo && q.preferredAnswer != null ? (q.preferredAnswer ? 'yes' : 'no') : undefined,

      // multi-choice preferred answers are embedded in options.isPreferredAnswer above
      //
      // video-response only fields — API rejects these on other question types
      ...(isVideoResponse && {
        maximumVideoDuration: q.maximumVideoDuration,
        isSkippable: q.isSkippable,
      }),
      // document-upload only fields
      ...(isDocumentUpload && {
        maximumDocumentCount: q.maximumDocumentCount,
        isSkippable: q.isSkippable,
      }),
    };

    entities.elements[id] = element;

    // --- Recurse into followups, grouped by branch ---
    // Group followups by their optionId, which maps to a branch
    const followupsByOption: Record<string, any[]> = {};
    followups.forEach((child: any) => {
      const key = child.optionId ?? '__unset__';
      if (!followupsByOption[key]) followupsByOption[key] = [];
      followupsByOption[key].push(child);
    });

    Object.entries(followupsByOption).forEach(([optionId, children]) => {
      // Find which branch name (option text) this optionId corresponds to
      const matchingOption = options.find((o: any) => o.id === optionId);
      const branchName = matchingOption?.text?.toLowerCase() ?? optionId;

      const orderingKey = `${id}-${branchName}`;
      const childIds = normalizeLevel(children, entities, ordering, id, branchName, isCopy);
      ordering[orderingKey] = childIds;
    });

    return id;
  });
};

/**
 * Entry point: normalizes a full API template response into the store shape.
 *
 * @param apiTemplate  - Raw API response object
 * @param isCopy       - When true, all IDs are replaced with fresh UUIDs (for copy flow)
 */
export const normalizeTemplate = (
  apiTemplate: any,
  isCopy = false,
): {
  entities: QuestionnaireEntities;
  ordering: QuestionnaireOrdering;
  templateName: string;
  selectedLanguage: string;
} => {
  const entities: QuestionnaireEntities = { elements: {} };
  const ordering: QuestionnaireOrdering = { root: [] };

  const rootIds = normalizeLevel(apiTemplate.questions ?? [], entities, ordering, null, null, isCopy);

  ordering.root = rootIds;

  Object.values(entities.elements).forEach((element) => {
    delete element.isEdited;
  });

  return {
    entities,
    ordering,
    templateName: isCopy ? `${apiTemplate.name ?? /* istanbul ignore next */ ''} (Copy)` : apiTemplate.name ?? '',
    selectedLanguage: apiTemplate.questionLocale ?? 'en',
  };
};
